import { useMemo, useCallback } from "react";
import { DAYS, getDaysInMonth, getFirstDayOfMonth } from "./constants";

interface CalendarCell {
  day: number;
  isCurrentMonth: boolean;
  isPrevMonth: boolean;
  isNextMonth: boolean;
  isDisabled: boolean;
}

interface CalendarGridProps {
  displayYear: number;
  displayMonth: number;
  selectedDate: Date | null;
  today: Date;
  minDate?: Date;
  onDayClick: (cell: CalendarCell) => void;
}

export function CalendarGrid({
  displayYear,
  displayMonth,
  selectedDate,
  today,
  minDate,
  onDayClick,
}: CalendarGridProps) {
  const daysInMonth = getDaysInMonth(displayYear, displayMonth);
  const firstDay = getFirstDayOfMonth(displayYear, displayMonth);
  const totalCells = 35;

  const cells = useMemo(() => {
    const result: CalendarCell[] = [];
    const prevMonthDays = getDaysInMonth(displayYear, displayMonth - 1);
    const minDateStart = minDate
      ? new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
      : null;

    const prevMonth = displayMonth === 0 ? 11 : displayMonth - 1;
    const prevYear = displayMonth === 0 ? displayYear - 1 : displayYear;

    for (let i = 0; i < firstDay; i++) {
      const day = prevMonthDays - firstDay + i + 1;
      const cellDate = new Date(prevYear, prevMonth, day);
      const isPast = minDateStart ? cellDate < minDateStart : false;
      result.push({
        day,
        isCurrentMonth: false,
        isPrevMonth: true,
        isNextMonth: false,
        isDisabled: isPast,
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const cellDate = new Date(displayYear, displayMonth, day);
      const isPast = minDateStart ? cellDate < minDateStart : false;
      result.push({
        day,
        isCurrentMonth: true,
        isPrevMonth: false,
        isNextMonth: false,
        isDisabled: isPast,
      });
    }

    const nextMonth = displayMonth === 11 ? 0 : displayMonth + 1;
    const nextYear = displayMonth === 11 ? displayYear + 1 : displayYear;

    const remaining = totalCells - result.length;
    for (let i = 1; i <= remaining; i++) {
      const cellDate = new Date(nextYear, nextMonth, i);
      const isPast = minDateStart ? cellDate < minDateStart : false;
      result.push({
        day: i,
        isCurrentMonth: false,
        isPrevMonth: false,
        isNextMonth: true,
        isDisabled: isPast,
      });
    }

    return result;
  }, [displayYear, displayMonth, firstDay, daysInMonth, minDate]);

  const rows = useMemo(() => {
    const result = [];
    for (let i = 0; i < cells.length; i += 7) {
      result.push(cells.slice(i, i + 7));
    }
    return result;
  }, [cells]);

  const isSelected = useCallback(
    (day: number, isCurrentMonth: boolean) => {
      if (!isCurrentMonth || !selectedDate) return false;
      return (
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === displayMonth &&
        selectedDate.getFullYear() === displayYear
      );
    },
    [selectedDate, displayMonth, displayYear],
  );

  const isToday = useCallback(
    (day: number, isCurrentMonth: boolean) => {
      if (!isCurrentMonth) return false;
      return (
        today.getDate() === day &&
        today.getMonth() === displayMonth &&
        today.getFullYear() === displayYear
      );
    },
    [today, displayMonth, displayYear],
  );

  return (
    <>
      {/* Day names */}
      <div className="grid grid-cols-7 mb-2">
        {DAYS.map((day, i) => (
          <div
            key={i}
            className="h-6 flex items-center justify-center text-[12px] text-[#B0B0B0] uppercase"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="mb-4">
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-7 mb-1">
            {row.map((cell, cellIndex) => {
              const selected = isSelected(cell.day, cell.isCurrentMonth);
              const todayCell = isToday(cell.day, cell.isCurrentMonth);
              return (
                <div
                  key={cellIndex}
                  onClick={() => !cell.isDisabled && onDayClick(cell)}
                  className={`
                    h-10 w-10 flex items-center justify-center text-[18px] font-normal
                    transition-all duration-150
                    ${
                      cell.isDisabled
                        ? "text-[#B0B0B0] cursor-not-allowed"
                        : "cursor-pointer hover:bg-[#FFAA72] hover:text-black hover:rounded"
                    }
                    ${cell.isPrevMonth || cell.isNextMonth ? "text-[#B0B0B0]" : ""}
                    ${
                      cell.isCurrentMonth && !cell.isDisabled && !selected
                        ? "hover:bg-neutral/5 rounded"
                        : ""
                    }
                    ${selected ? "bg-[#FFAA72] text-black rounded" : ""}
                    ${todayCell && !selected ? "border border-neutral" : ""}
                  `}
                  // style={{
                  //   borderRadius: selected
                  //     ? "1000px"
                  //     : todayCell
                  //     ? "1000px"
                  //     : undefined,
                  // }}
                >
                  {cell.day}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}
